import { Box, Button, Chip, IconButton, Stack, Typography } from '@mui/material';
import { SupportPolicy } from '@provision/entity-types';
import { ServiceLevel } from '@provision/service-level-utils';
import {
  EscalationRule,
  formatRuleResponseLabel,
  parseEscalationRules,
  SERVICE_LEVEL_PRIORITY_ORDER,
} from '@provision/sidekick-utils';
import dayjs, { Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import { useForm, useFormContext, useWatch } from 'react-hook-form';
import { isNullish } from 'remeda';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalContainer, useModalSteps } from 'src/components/ModalContainer';
import { SignalUrgencyLevel } from 'src/graphql/entities/signal-business-hours/signal-business-hours.types';
import { deserializeSupportPolicyConditions } from 'src/graphql/entities/support-policy/support-policy.conditions';
import {
  NormalizedSupportPolicy,
  normalizeSupportPolicyScopeFields,
} from 'src/graphql/entities/support-policy/support-policy.utils';
import { getDefaultEscalationTitle } from './escalation-defaults';
import { EscalationRuleStep } from './escalation-rule-step';
import {
  ESCALATION_URGENCY_DISPLAY_LABELS,
  formatEnumLabel,
  formatServiceLevelLabel,
  serializeEscalationRules,
} from './escalation-utils';
import { POLICY_CATEGORY_ESCALATION } from './policy-constants';
import { PolicyUserGroupsField, SupportPolicyFormValues } from './policy-form';
import { RuleMatchPreview } from './rules-match-preview';
import { useEscalationEvaluation } from './use-escalation-evaluation';

interface EscalationPolicyFormProps {
  policy?: Partial<SupportPolicy> | Partial<NormalizedSupportPolicy>;
  onSave: (values: SupportPolicyFormValues) => void;
  onClose: () => void;
}

const ruleListSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  p: 1.5,
};

const ruleItemSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  p: 1,
};

interface EscalationRulesSectionProps {
  partnerId?: number;
  urgencyLevel: string;
}

const EscalationRulesSection = ({ partnerId, urgencyLevel }: EscalationRulesSectionProps) => {
  const { pushStep } = useModalSteps();
  const { control, setValue } = useFormContext<SupportPolicyFormValues>();
  const evalCtx = useEscalationEvaluation(partnerId);
  const [previewServiceLevel, setPreviewServiceLevel] = useState<string>(
    SERVICE_LEVEL_PRIORITY_ORDER.at(-1) ?? ServiceLevel.Signature,
  );
  const [previewTime, setPreviewTime] = useState<Dayjs>(dayjs());

  const rawEscalationRules = useWatch({ control, name: 'escalationRules' });
  const escalationRules = useMemo(
    () => parseEscalationRules(rawEscalationRules),
    [rawEscalationRules],
  );

  const setRules = (rules: EscalationRule[], shouldDirty = false) => {
    setValue('escalationRules', serializeEscalationRules(rules), { shouldDirty });
  };

  const openRuleStep = async (ruleIndex: number | null) => {
    const rule = await pushStep<EscalationRule>(
      <EscalationRuleStep
        initialRule={isNullish(ruleIndex) ? null : escalationRules[ruleIndex]}
        order={ruleIndex ?? escalationRules.length}
        urgencyLevel={urgencyLevel}
      />,
    );
    if (!rule) {
      return;
    }

    const nextRules = [...escalationRules];
    if (isNullish(ruleIndex)) {
      nextRules.push(rule);
    } else {
      nextRules[ruleIndex] = rule;
    }
    setRules(nextRules, true);
  };

  return (
    <>
      <Box sx={ruleListSx}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="subtitle2">Response Rules</Typography>
            <Typography variant="caption" color="text.secondary">
              Rules are evaluated top-to-bottom. First match wins.
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            data-testid="escalation-policy-add-rule"
            onClick={() => void openRuleStep(null)}
          >
            Add Rule
          </Button>
        </Stack>

        <Stack spacing={1} sx={{ mt: 1 }}>
          {escalationRules.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No rules configured. Click &quot;Add Rule&quot; to get started.
            </Typography>
          ) : null}

          {escalationRules.map((rule, index) => (
            <Box
              key={rule.id}
              data-testid="escalation-policy-rule"
              data-rule-id={rule.id}
              sx={ruleItemSx}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  {rule.name ? (
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ mb: 0.25 }}
                      data-testid="escalation-policy-rule-name"
                    >
                      {rule.name}
                    </Typography>
                  ) : null}
                  <Typography variant="body2" color={rule.name ? 'text.secondary' : undefined}>
                    If <strong>{formatServiceLevelLabel(rule.serviceLevels)}</strong> escalates{' '}
                    <strong>{formatEnumLabel(rule.timeCondition.type)}</strong>, then{' '}
                    <strong>{formatRuleResponseLabel(rule)}</strong>
                  </Typography>
                </Box>

                <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                  <IconButton
                    size="small"
                    aria-label="Move rule up"
                    data-testid="escalation-policy-rule-move-up"
                    disabled={index === 0}
                    onClick={() => setRules(moveRule(escalationRules, index, index - 1))}
                  >
                    <DynamicIcon name="keyboard_arrow_up" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="Move rule down"
                    data-testid="escalation-policy-rule-move-down"
                    disabled={index === escalationRules.length - 1}
                    onClick={() => setRules(moveRule(escalationRules, index, index + 1))}
                  >
                    <DynamicIcon name="keyboard_arrow_down" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="Edit rule"
                    data-testid="escalation-policy-rule-edit"
                    onClick={() => void openRuleStep(index)}
                  >
                    <DynamicIcon name="edit" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="Delete rule"
                    data-testid="escalation-policy-rule-delete"
                    onClick={() =>
                      setRules(escalationRules.filter((_, ruleIndex) => ruleIndex !== index))
                    }
                  >
                    <DynamicIcon name="delete" />
                  </IconButton>
                </Stack>
              </Stack>

              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Chip size="small" label={`Order ${index + 1}`} />
                <Chip
                  size="small"
                  label={formatEnumLabel(rule.response.type)}
                  variant="outlined"
                />
              </Stack>
            </Box>
          ))}
        </Stack>
      </Box>

      <RuleMatchPreview
        rules={escalationRules}
        serviceLevel={previewServiceLevel}
        onServiceLevelChange={setPreviewServiceLevel}
        currentTime={previewTime}
        onCurrentTimeChange={setPreviewTime}
        timezone={evalCtx.timezone}
        businessHours={evalCtx.businessHours}
        escalationCutoffHours={evalCtx.escalationCutoffHours}
        holidays={evalCtx.holidays}
      />
    </>
  );
};

export const EscalationPolicyForm = ({
  policy,
  onSave,
  onClose,
}: EscalationPolicyFormProps) => {
  const defaultRules = useMemo(() => parseEscalationRules(policy?.escalationRules), [policy]);
  const scope = useMemo(() => normalizeSupportPolicyScopeFields(policy), [policy]);

  const formMethods = useForm<SupportPolicyFormValues>({
    mode: 'onChange',
    defaultValues: {
      title: policy?.title ?? '',
      description: policy?.description ?? '',
      type: policy?.type ?? 'requirement',
      category: POLICY_CATEGORY_ESCALATION,
      pinnedAt: Boolean(policy?.pinnedAt),
      validUntil: policy?.validUntil ? dayjs(policy.validUntil as string | Date) : null,
      userGroups: policy?.userGroups?.map((group) => group.id) ?? [],
      addressIds: scope.addressIds,
      clientIds: scope.clientIds,
      clientToAddressIds: scope.clientToAddressIds,
      enableConditions: Boolean(policy?.conditions),
      conditions: deserializeSupportPolicyConditions(policy?.conditions),
      urgencyLevel: policy?.urgencyLevel ?? SignalUrgencyLevel.Normal,
      escalationRules: serializeEscalationRules(defaultRules),
    },
  });
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = formMethods;

  const urgencyLevel = useWatch({ control, name: 'urgencyLevel' });
  const urgencyLabel =
    ESCALATION_URGENCY_DISPLAY_LABELS[urgencyLevel ?? ''] ?? urgencyLevel ?? 'Escalation';

  const handleFormSubmit = (values: SupportPolicyFormValues) => {
    const rules = parseEscalationRules(values.escalationRules);

    onSave({
      ...values,
      title: getDefaultEscalationTitle(values.urgencyLevel ?? SignalUrgencyLevel.Normal),
      description: generateDescriptionFromRules(rules),
    });
  };

  return (
    <ModalContainer
      open
      size="large"
      title="Edit escalation policy"
      data-testid="support-policy-modal"
      onClose={onClose}
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'support-policy-modal-cancel' },
        {
          label: 'Save policy',
          kind: 'primary',
          onClick: handleSubmit(handleFormSubmit),
          disabled: !isValid,
          testId: 'support-policy-modal-save',
        },
      ]}
    >
      <GenericForm<SupportPolicyFormValues>
        formMethods={formMethods}
        data-testid="escalation-policy-form"
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {urgencyLabel} Policy
        </Typography>

        <Stack gap={0.5}>
          <Typography variant="caption" color="text.secondary">
            Urgency level
          </Typography>
          <Typography variant="body2">{urgencyLabel}</Typography>
        </Stack>

        <PolicyUserGroupsField testId="escalation-policy-user-groups" />

        <EscalationRulesSection
          partnerId={(policy as Partial<NormalizedSupportPolicy>)?.partnerId}
          urgencyLevel={urgencyLevel ?? SignalUrgencyLevel.Normal}
        />
      </GenericForm>
    </ModalContainer>
  );
};

function moveRule(rules: EscalationRule[], from: number, to: number): EscalationRule[] {
  const next = [...rules];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next.map((rule, index) => ({ ...rule, order: index }));
}

function generateDescriptionFromRules(rules: EscalationRule[]): string {
  if (rules.length === 0) return '';

  const bullets = rules.map((rule) => {
    const serviceLevelText = formatServiceLevelLabel(rule.serviceLevels);
    const timeText = formatEnumLabel(rule.timeCondition.type);
    const responseText = formatRuleResponseLabel(rule);
    return `<li>${serviceLevelText} (${timeText}): ${responseText}</li>`;
  });

  return `<ul>${bullets.join('')}</ul>`;
}
